import { splitCsvLine } from "../../shared/functions/splitCsvLine";
import { assignmentDatePairErrors } from "./assignmentDates";
import { deriveRowDecisions } from "./deriveRowDecisions";
import type {
  MatchingCsvAction,
  MatchingParseError,
  MatchingParseOutcome,
  MatchingRow,
} from "../types/matching";

export const MAX_ROWS = 500;

function normaliseHeader(h: string): string {
  return h.toLowerCase().replace(/[\s_-]/g, "");
}

const COL_MATCH_ACTION = "matchaction";
const COL_EMPLOYEE_CODE = "employeecode";
const COL_COMPONENT_CODE = "componentcode";
const COL_COMPONENT_NAME = "componentname";
const COL_START_DATE = "startdate";
const COL_FINISH_DATE = "finishdate";
const COL_TARGET_UNITS = "targetunits";
const COL_TARGET_AMOUNT = "targetamount";
/** Legacy alias accepted until D-CSV-SHAPE locks names. */
const COL_AMOUNT_ALIAS = "amount";

const FILE_LEVEL_BLOCKED_ALWAYS = new Set([
  "matchkind",
  "companycode",
  "paygroupcode",
  "payperiodenddate",
  "overrideflag",
  "hours",
  "units",
  "unit",
  "quantity",
  "qty",
  "summary",
  "grosspay",
  "netpay",
]);

const COL_PAY_RUN_ID = "payrunid";

function parseAmount(raw: string): number | null | "invalid" {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const cleaned = trimmed.replace(/[$,\s]/g, "");
  if (cleaned === "" || cleaned === "-") return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return "invalid";
  return n;
}

function isBlankRow(cells: string[]): boolean {
  return cells.every((c) => c.trim() === "");
}

const MATCH_ACTION_HINT =
  "use APPLY, REAPPLY, DELETE, or DELETE_KEEP_AMOUNT";

function parseMatchAction(raw: string): MatchingCsvAction | "invalid" | "blank" {
  const v = raw
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (v === "") return "blank";
  if (v === "APPLY") return "APPLY";
  if (v === "DELETE") return "DELETE";
  if (v === "REAPPLY") return "REAPPLY";
  if (v === "DELETE_KEEP_AMOUNT") return "DELETE_KEEP_AMOUNT";
  return "invalid";
}

export interface ParseMatchingCsvOptions {
  /** Bound from portal UI and stamped onto every row (PayRunId is not a CSV column). */
  boundPayRunId?: string | null;
}

/**
 * Parse matching CSV (bind-pay-run-first).
 * PayRunId column is rejected; every row is stamped with boundPayRunId.
 * StartDate/FinishDate are optional but must both be set or both omitted.
 */
export function parseMatchingCsv(
  text: string,
  options: ParseMatchingCsvOptions | string
): MatchingParseOutcome {
  const opts: ParseMatchingCsvOptions =
    typeof options === "string" ? { boundPayRunId: options } : options;
  const boundPayRunId = opts.boundPayRunId ?? null;

  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/);
  while (lines.length > 0 && lines[lines.length - 1].trim() === "") {
    lines.pop();
  }
  if (lines.length === 0) {
    return {
      ok: false,
      errors: [{ fileRow: null, message: "File is empty." }],
    };
  }

  // Skip illustrative comment row if present (golden fixture style).
  let headerLineIndex = 0;
  const first = lines[0].trim();
  if (
    first.toUpperCase().startsWith("ILLUSTRATIVE") ||
    (!first.toLowerCase().includes("employeecode") &&
      lines.length > 1 &&
      normaliseHeader(splitCsvLine(lines[1])[0] ?? "").includes("match"))
  ) {
    // keep header at 0 unless first line clearly isn't headers
  }
  if (!normaliseHeader(splitCsvLine(lines[0])[0] ?? "").includes("match") &&
      !normaliseHeader(splitCsvLine(lines[0])[0] ?? "").includes("employee")) {
    // If first cell isn't a known header, try line 1 (skip legend).
    const secondKeys = splitCsvLine(lines[1] ?? "").map(normaliseHeader);
    if (
      secondKeys.includes(COL_EMPLOYEE_CODE) ||
      secondKeys.includes(COL_MATCH_ACTION)
    ) {
      headerLineIndex = 1;
    }
  }

  const headerCells = splitCsvLine(lines[headerLineIndex]);
  const headerKeys = headerCells.map(normaliseHeader);
  const errors: MatchingParseError[] = [];

  for (let i = 0; i < headerKeys.length; i++) {
    const key = headerKeys[i];
    if (FILE_LEVEL_BLOCKED_ALWAYS.has(key)) {
      errors.push({
        fileRow: headerLineIndex + 1,
        message:
          `Column "${headerCells[i]}" is not supported ` +
          "(AMOUNT matching only — remove MatchKind / hours / SUMMARY columns).",
      });
    }
    if (key === COL_PAY_RUN_ID) {
      errors.push({
        fileRow: headerLineIndex + 1,
        message:
          'Column "PayRunId" is not used — bind the pay run in the UI, then upload.',
      });
    }
  }

  const indexOf = (key: string) => headerKeys.indexOf(key);
  const amountIdx = (() => {
    const a = indexOf(COL_TARGET_AMOUNT);
    return a >= 0 ? a : indexOf(COL_AMOUNT_ALIAS);
  })();
  const payRunIdx = indexOf(COL_PAY_RUN_ID);

  if (!boundPayRunId) {
    errors.push({
      fileRow: null,
      message: "Bind a pay run before uploading.",
    });
  }
  if (indexOf(COL_MATCH_ACTION) < 0) {
    errors.push({
      fileRow: headerLineIndex + 1,
      message: "Missing required column: MatchAction.",
    });
  }
  if (indexOf(COL_EMPLOYEE_CODE) < 0) {
    errors.push({
      fileRow: headerLineIndex + 1,
      message: "Missing required column: EmployeeCode.",
    });
  }
  if (indexOf(COL_COMPONENT_NAME) < 0) {
    errors.push({
      fileRow: headerLineIndex + 1,
      message: "Missing required column: ComponentName.",
    });
  }
  if (amountIdx < 0) {
    errors.push({
      fileRow: headerLineIndex + 1,
      message: "Missing required column: TargetAmount.",
    });
  }
  if (errors.length > 0) {
    return { ok: false, errors };
  }

  const actionIdx = indexOf(COL_MATCH_ACTION);
  const empIdx = indexOf(COL_EMPLOYEE_CODE);
  const codeIdx = indexOf(COL_COMPONENT_CODE);
  const nameIdx = indexOf(COL_COMPONENT_NAME);
  const startIdx = indexOf(COL_START_DATE);
  const finishIdx = indexOf(COL_FINISH_DATE);
  const unitsIdx = indexOf(COL_TARGET_UNITS);

  const known = new Set(
    [
      payRunIdx,
      actionIdx,
      empIdx,
      codeIdx,
      nameIdx,
      startIdx,
      finishIdx,
      unitsIdx,
      amountIdx,
    ].filter((i) => i >= 0)
  );
  const rows: MatchingRow[] = [];

  const pushStub = (
    fileRow: number,
    cells: string[],
    overrides: {
      matchAction?: MatchingCsvAction;
      rawMatchAction?: string;
      payRunId?: string;
      employeeCode?: string;
      componentCode?: string | null;
      componentName?: string;
      startDate?: string | null;
      finishDate?: string | null;
      targetAmount?: number | null;
      rawAmount?: string;
    }
  ) => {
    const matchAction = overrides.matchAction ?? "APPLY";
    const amount = overrides.targetAmount ?? null;
    const decisions = deriveRowDecisions(matchAction, amount);
    const extraColumns: Record<string, string> = {};
    headerCells.forEach((header, colIdx) => {
      if (!known.has(colIdx) && header.trim() !== "") {
        extraColumns[header] = cells[colIdx] ?? "";
      }
    });
    const rawUnits = unitsIdx >= 0 ? (cells[unitsIdx] ?? "").trim() : "";
    if (rawUnits) {
      // Preserved for issues CSV / AC-22 warning; not sent as a write amount.
      extraColumns.TargetUnits = rawUnits;
    }
    rows.push({
      fileRow,
      payRunId: overrides.payRunId ?? (boundPayRunId as string) ?? "",
      matchAction,
      rawMatchAction: overrides.rawMatchAction ?? matchAction,
      employeeCode: overrides.employeeCode ?? "",
      componentCode: overrides.componentCode ?? null,
      componentName: overrides.componentName ?? "",
      startDate: overrides.startDate ?? null,
      finishDate: overrides.finishDate ?? null,
      targetAmount: amount,
      rawAmount: overrides.rawAmount ?? "",
      manualPay: decisions.manualPay,
      amountMode: decisions.amountMode,
      action: decisions.action,
      extraColumns,
    });
  };

  for (let i = headerLineIndex + 1; i < lines.length; i++) {
    const fileRow = i + 1;
    const cells = splitCsvLine(lines[i]);
    if (isBlankRow(cells)) continue;

    const matchActionRaw = actionIdx >= 0 ? (cells[actionIdx] ?? "") : "";
    const matchActionParsed = parseMatchAction(matchActionRaw);
    const rowPayRunId = boundPayRunId as string;
    const employeeCode = (cells[empIdx] ?? "").trim();
    const componentName = (cells[nameIdx] ?? "").trim();
    const componentCode =
      codeIdx >= 0 ? (cells[codeIdx] ?? "").trim() || null : null;
    const startDate =
      startIdx >= 0 ? (cells[startIdx] ?? "").trim() || null : null;
    const finishDate =
      finishIdx >= 0 ? (cells[finishIdx] ?? "").trim() || null : null;
    const rawAmount = cells[amountIdx] ?? "";
    const amountParsed = parseAmount(rawAmount);
    const amountForStub =
      amountParsed === "invalid" ? null : amountParsed;

    const stubBase = {
      matchAction:
        matchActionParsed === "invalid" || matchActionParsed === "blank"
          ? ("APPLY" as const)
          : matchActionParsed,
      rawMatchAction: matchActionRaw.trim() || matchActionRaw,
      payRunId: rowPayRunId,
      employeeCode,
      componentCode,
      componentName,
      startDate,
      finishDate,
      targetAmount: amountForStub,
      rawAmount,
    };

    if (matchActionParsed === "blank") {
      errors.push({
        fileRow,
        message: `MatchAction is required — ${MATCH_ACTION_HINT}.`,
      });
      pushStub(fileRow, cells, stubBase);
      continue;
    }

    if (matchActionParsed === "invalid") {
      errors.push({
        fileRow,
        message: `Unknown MatchAction "${matchActionRaw.trim()}" — ${MATCH_ACTION_HINT}.`,
      });
      pushStub(fileRow, cells, stubBase);
      continue;
    }

    if (!employeeCode) {
      errors.push({ fileRow, message: "EmployeeCode is required." });
      pushStub(fileRow, cells, stubBase);
      continue;
    }
    if (!componentName) {
      errors.push({ fileRow, message: "ComponentName is required." });
      pushStub(fileRow, cells, stubBase);
      continue;
    }
    const datePairErrors = assignmentDatePairErrors(
      startDate ?? "",
      finishDate ?? ""
    );
    if (datePairErrors.length > 0) {
      for (const message of datePairErrors) {
        errors.push({ fileRow, message });
      }
      pushStub(fileRow, cells, stubBase);
      continue;
    }

    if (amountParsed === "invalid") {
      errors.push({
        fileRow,
        message: `TargetAmount "${rawAmount.trim()}" is not a valid number.`,
      });
      pushStub(fileRow, cells, stubBase);
      continue;
    }
    if (matchActionParsed === "APPLY" && amountParsed === null) {
      errors.push({
        fileRow,
        message: "TargetAmount is required for APPLY rows.",
      });
      pushStub(fileRow, cells, stubBase);
      continue;
    }
    if (
      (matchActionParsed === "REAPPLY" ||
        matchActionParsed === "DELETE_KEEP_AMOUNT") &&
      amountParsed !== null
    ) {
      errors.push({
        fileRow,
        message:
          `TargetAmount must be blank for ${matchActionParsed}. ` +
          "Use APPLY to change the amount.",
      });
      pushStub(fileRow, cells, stubBase);
      continue;
    }

    const decisions = deriveRowDecisions(matchActionParsed, amountParsed);
    const extraColumns: Record<string, string> = {};
    headerCells.forEach((header, colIdx) => {
      if (!known.has(colIdx) && header.trim() !== "") {
        extraColumns[header] = cells[colIdx] ?? "";
      }
    });
    const rawUnits = unitsIdx >= 0 ? (cells[unitsIdx] ?? "").trim() : "";
    if (rawUnits) {
      extraColumns.TargetUnits = rawUnits;
    }

    rows.push({
      fileRow,
      payRunId: rowPayRunId,
      matchAction: matchActionParsed,
      rawMatchAction: matchActionRaw.trim() || matchActionParsed,
      employeeCode,
      componentCode,
      componentName,
      startDate,
      finishDate,
      targetAmount: amountParsed,
      rawAmount,
      manualPay: decisions.manualPay,
      amountMode: decisions.amountMode,
      action: decisions.action,
      extraColumns,
    });
  }

  if (rows.length > MAX_ROWS) {
    return {
      ok: false,
      errors: [
        {
          fileRow: null,
          message: `File has ${rows.length} rows; maximum is ${MAX_ROWS}.`,
        },
      ],
    };
  }
  if (errors.length > 0) {
    return {
      ok: false,
      errors,
      ...(rows.length > 0 ? { rows } : {}),
    };
  }
  if (rows.length === 0) {
    return {
      ok: false,
      errors: [{ fileRow: null, message: "No data rows found." }],
    };
  }
  return { ok: true, rows };
}
